import { pluginsApi } from "@/Api/Plugins";
import { pushToStoreList } from "@/Store/StoreExternalAccess";
import { PopupVariant, } from "@/UI/MessageAlerts/Popups/InterfacesAndEnums";
// Import startWaitSpinner as startWaitSpinnerSrc
import { startWaitSpinner as startWaitSpinnerSrc } from "@/UI/MessageAlerts/WaitSpinner";
import { stopWaitSpinner as stopWaitSpinnerSrc } from "@/UI/MessageAlerts/WaitSpinner";
import { stopAllWaitSpinners as stopAllWaitSpinnersSrc } from "@/UI/MessageAlerts/WaitSpinner";
import { describeParameters } from "@/UI/Panels/Log/LogUtils";
import { addToast, clearAllToasts, } from "@/UI/MessageAlerts/Toasts/ToastManager";
import { appName } from "@/Core/GlobalVars";
import { sanitizeHtml } from "@/Core/Security/Sanitize";
export const messagesApi = {
    /**
     * Displays a popup message or a toast notification.
     *
     * @param  {string}        title                        The title of the
     *                                                      popup.
     * @param  {string}        message                      The message to
     *                                                      display.
     * @param  {PopupVariant}  [variant]  The type of message.
     * @param  {Function}      [callBack]                   A function to call
     *                                                      when the popup is
     *                                                      closed.
     * @param  {boolean}       [neverClose]           If true, the modal
     *                                                      can never be closed.
     * @param {IToastOptions}  [toastParams]                If provided,
     *                                                      displays a toast
     *                                                      instead of a modal,
     *                                                      with given options.
     */
    popupMessage: async function (title, message = "", variant = PopupVariant.Info, callBack = undefined, neverClose = false, toastParams) {
        // Sanitize title and message to prevent XSS attacks.
        const sanitizedTitle = await sanitizeHtml(title);
        const sanitizedMessage = await sanitizeHtml(message);
        if (toastParams) {
            if (neverClose) {
                toastParams.duration = 0; // Never autohides
                toastParams.showCloseBtn = false; // No close button
            }
            addToast(sanitizedTitle, sanitizedMessage, variant, callBack, toastParams);
            return;
        }
        // If popup already open, do not open another one.
        pluginsApi.runPlugin("simplemsg", {
            title: sanitizedTitle,
            message: sanitizedMessage,
            variant,
            neverClose,
            callBack,
            open: true, // open
        });
    },
    /**
     * Closes the popup message.
     */
    closePopupMessage: function () {
        pluginsApi.runPlugin("simplemsg", {
            title: "",
            message: "",
            open: false, // close
        });
        clearAllToasts();
    },
    /**
     * Displays an error popup.
     *
     * @param  {string}   message     The error message.
     * @param  {Function} [callBack]  A function to call when the popup is
     *                                closed.
     */
    popupError: function (message, callBack) {
        this.popupMessage(appName + " Error", message, PopupVariant.Danger, callBack);
        // Also throw the error to the console, unless it's a test. If you throw
        // this error in a test, it will fail the test, but sometimes failure
        // means the test passes.
        // if (!isTest) {
        //     throw new Error(message);
        // }
    },
    /**
     * Displays a Yes-No popup message.
     *
     * @param  {string}  message          The message to display.
     * @param  {string}  [title]          The title of the popup.
     * @param  {string}  [yesBtnTxt]      The text to use for the yes button.
     * @param  {string}  [noBtnTxt]       The text to use for the no button.
     * @param  {boolean} [showCancelBtn]  Whether to show the cancel button.
     * @returns {Promise<YesNo>}  Resolves to YesNo.Yes, YesNo.No, or YesNo.Cancel.
     */
    popupYesNo: async function (message, title, yesBtnTxt, noBtnTxt, showCancelBtn) {
        return await new Promise((resolve, reject) => {
            // Add reject parameter
            try {
                pluginsApi.runPlugin("yesnomsg", {
                    title,
                    message,
                    /**
                     * Callback function to handle the user's response.
                     *
                     * @param {YesNo} val - The user's response (Yes, No, or Cancel).
                     * @returns {void}
                     */
                    callBack: (val) => {
                        resolve(val);
                    },
                    yesBtnTxt,
                    noBtnTxt,
                    showCancelBtn,
                });
            }
            catch (error) {
                // Reject the promise if the plugin fails to run
                reject(error);
            }
        });
    },
    /**
     * Displays a popup message.
     *
     * @param  {string}       title                  The title of the popup.
     * @param  {string}       message                The message to display.
     * @param  {ITableData}   tableData              The table data to display.
     * @param  {string}       caption                The caption of the table.
     * @param  {number}       [precision]          The number of decimal
     *                                               places to display.
     * @param  {string}       [downloadFilenameBase] The base filename for
     *                                               downloads.
     */
    popupTableData: async function (title, message, tableData, caption, precision = 3, downloadFilenameBase) {
        const sanitizedTitle = await sanitizeHtml(title);
        const sanitizedMessage = await sanitizeHtml(message);
        // If popup already open, do not open another one.
        pluginsApi.runPlugin("tabledatapopup", {
            title: sanitizedTitle,
            message: sanitizedMessage,
            tableData,
            caption,
            precision,
            downloadFilenameBase: downloadFilenameBase || caption,
            open: true, // open
        });
    },
    /**
     * Starts a wait spinner. Returns an id that can be used to stop the spinner.
     *
     * @param {number} [timeOut]  The timeout in milliseconds.
     * @returns {string}  The id of the spinner.
     */
    startWaitSpinner: function (timeOut = 30000) {
        return startWaitSpinnerSrc(timeOut);
    },
    /**
     * Stops a wait spinner.
     *
     * @param {string} id  The id of the spinner.
     */
    stopWaitSpinner: function (id) {
        stopWaitSpinnerSrc(id);
    },
    /**
     * Stops all wait spinners.
     */
    stopAllWaitSpinners: function () {
        stopAllWaitSpinnersSrc();
    },
    /**
     * Logs a message. Appears in the log at the bottom of the screen.
     *
     * @param {string} message       The message to log.
     * @param {any}    [parameters]  The parameters associated with this log, if
     *                               any.
     * @param {string} [jobId]       The job ID associated with this log, if
     *                               any.
     */
    log: function (message, parameters, jobId) {
        // Get current local time as string. Like "2020-01-01 12:00"
        const now = new Date();
        const timestamp = now.toLocaleString("en-US", {
            hour12: true,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
        const timestampInt = Math.round(now.getTime() / 1000);
        const paramDesc = describeParameters(parameters);
        // Add the message to the log
        pushToStoreList("log", {
            timestamp,
            timestampSecs: timestampInt,
            message,
            parameters: paramDesc,
            jobId: jobId,
        });
    },
};
